import { useTranslation } from "react-i18next";
import { Typography, makeStyles } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import dayjs from "dayjs";
import type { FileMetadata } from "@dashboard-v3/api";
import { useFetch } from "utils/api/useFetch";
import DetailFields, { Field } from "./DetailFields";
import { TFunction } from "i18next";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";

interface Props {
  fileId: string;
}

export default function EmailMetadataSection({ fileId }: Props) {
  const { t } = useTranslation("mxheroShares");
  const classes = useStyles();
  const query = new URLSearchParams({ fileId });
  const { data, error, isLoading } = useFetch<FileMetadata>(
    `/mxhero-shares/files/metadata?${query}`
  );

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {t("malware.errors.fetchMetadata")}
      </Typography>
    );
  }

  if (isLoading) return <Skeleton height={120} variant="rect" />;

  if (!data.mxhero) {
    return (
      <Typography variant="body2" color="textSecondary">
        {t("malware.detail.noEmail")}
      </Typography>
    );
  }

  return <DetailFields fields={getMetadataFields(data, classes, t)} />;
}

function getMetadataFields(
  data: FileMetadata,
  classes: ClassNameMap<"mono">,
  t: TFunction
): Field[] {
  if (!data) return [];

  const { mxhero: metadata } = data;
  const date = metadata.date
    ? dayjs(metadata.date).format("YYYY-MM-DD HH:mm")
    : "";

  return [
    { label: t("malware.detail.from"), value: metadata.from },
    { label: t("malware.detail.to"), value: metadata.to },
    { label: t("malware.detail.cc"), value: metadata.cc },
    { label: t("malware.detail.subject"), value: metadata.subject },
    { label: t("malware.detail.date"), value: date },
    {
      label: t("malware.detail.messageId"),
      value: <span className={classes.mono}>{metadata.messageId}</span>,
    },
  ];
}

const useStyles = makeStyles({
  mono: {
    fontFamily: "SFMono-Regular, Consolas, Menlo, monospace",
    fontSize: "0.78rem",
  },
});
